interface ParsedError {
  message: string;
  fieldErrors?: { [key: string]: string[] };
  status?: number;
}

export type ErrorResponse = {
  response?: {
    data?: {
      [key: string]: string[] | { [key: string]: string[] } | string;
    };
    status?: number;
    statusText?: string;
  };
  message?: string;
};

/**
 * Parses Django API error responses into a more user-friendly format
 * @param error - The error object from axios or similar HTTP client
 * @returns ParsedError object with formatted error messages
 */
export const parseDjangoError = (error: ErrorResponse): ParsedError => {
  // If error is already in our format, return it
  if (error.message && 'fieldErrors' in error) {
    return error as ParsedError;
  }

  // Initialize the result object
  const result: ParsedError = {
    message: 'An error occurred',
    fieldErrors: {},
  };

  // Handle axios error response
  if (error.response) {
    result.status = error.response.status;

    // Handle Django error format
    if (error.response.data) {
      const data = error.response.data;

      // Handle nested error structure
      if (typeof data === 'object') {
        // Flatten nested error objects
        const flattenErrors = (
          obj: Record<string, unknown>,
          prefix = ''
        ): { [key: string]: string[] } => {
          return Object.entries(obj).reduce((acc: { [key: string]: string[] }, [key, value]) => {
            const newKey = prefix ? `${prefix}.${key}` : key;

            if (Array.isArray(value)) {
              // Ensure all values are strings
              acc[newKey] = value.filter((v): v is string => typeof v === 'string');
            } else if (typeof value === 'object' && value !== null) {
              Object.assign(acc, flattenErrors(value as Record<string, unknown>, newKey));
            } else if (typeof value === 'string') {
              acc[newKey] = [value];
            }

            return acc;
          }, {});
        };

        const flattenedErrors = flattenErrors(data as Record<string, unknown>);

        // Set a user-friendly main message based on field errors
        const errorCount = Object.keys(flattenedErrors).length;
        if (errorCount > 0) {
          const firstFieldName = Object.keys(flattenedErrors)[0].split('.').pop() || '';
          const formattedFieldName = fieldNameMap[firstFieldName] || firstFieldName;

          if (errorCount === 1) {
            const firstError = Object.values(flattenedErrors)[0]?.[0];
            result.message = firstError || `There was an issue with ${formattedFieldName}.`;
          } else {
            result.message = `Please fix the ${errorCount} validation errors below.`;
          }
        } else {
          result.message = 'Please check your input and try again.';
        }

        // Add all field errors
        result.fieldErrors = flattenedErrors;
      } else if (typeof data === 'string') {
        result.message = data;
      }
    }
  } else if (error.message) {
    // Handle non-response errors (like network errors)
    if (error.message.toLowerCase().includes('network')) {
      result.message = 'Network error. Please check your connection and try again.';
    } else {
      result.message = error.message;
    }
  }

  return result;
};

/**
 * Maps backend field names to user-friendly field names
 */
const fieldNameMap: { [key: string]: string } = {
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  password: 'Password',
  confirm_password: 'Password confirmation',
  new_password: 'New password',
  current_password: 'Current password',
  phone: 'Phone number',
  dob: 'Date of birth',
  date_of_birth: 'Date of birth',
  address: 'Address',
  gender: 'Gender',
  occupation: 'Occupation',
  referred_by: 'Referred by',
  medicare: 'Medicare number',
  cur_medications: 'Current medications',
  medical_history: 'Medical history',
  prev_treatments: 'Previous treatments',
  emergency_contact: 'Emergency contact',
  emergency_phone: 'Emergency phone',
  insurance_provider: 'Insurance provider',
  insurance_number: 'Insurance number',
  allergies: 'Allergies',
  conditions: 'Medical conditions',
  field_of_practice: 'Field of practice',
  business_name: 'Business name',
  practice_management_software: 'Practice management software',
  location: 'Location',
  user_role: 'User role',
};

/**
 * Formats field errors into user-friendly messages
 * @param fieldErrors - Object containing field errors
 * @returns Formatted error message
 */
export const formatFieldErrors = (fieldErrors: { [key: string]: string[] }): string => {
  return Object.entries(fieldErrors)
    .map(([field, errors]) => {
      // Get the last part of the field name (e.g., 'user.phone' -> 'phone')
      const fieldName = field.split('.').pop() || field;
      // Use mapped name or capitalize first letter and replace underscores with spaces
      const formattedFieldName =
        fieldNameMap[fieldName] ||
        fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

      // Format error messages to be more user-friendly
      const formattedErrors = errors.map(error => {
        // Common Django error message improvements
        if (
          error.includes('already exists') ||
          error.includes('already registered') ||
          error.includes('already in use')
        ) {
          if (fieldName === 'email') {
            return 'This email is already in use. Please use a different email or try logging in.';
          }
          return `${formattedFieldName} is already in use. Please use a different ${fieldName.replace(/_/g, ' ')}.`;
        }
        if (
          error.includes('required') ||
          error.includes('This field may not be blank') ||
          error.includes('This field is required')
        ) {
          return `${formattedFieldName} is required.`;
        }
        if (error.includes('valid email') || error.includes('Enter a valid email')) {
          return 'Please enter a valid email address.';
        }
        if (
          error.includes('Passwords do not match') ||
          error.includes('password must match') ||
          error.includes('passwords must be identical')
        ) {
          return 'Passwords do not match. Please make sure both passwords are the same.';
        }
        if (error.includes('password is too common') || error.includes('too common')) {
          return 'This password is too common. Please choose a more unique password.';
        }
        if (error.includes('password is too similar') || error.includes('too similar to')) {
          return 'Your password is too similar to your personal information. Please choose a different password.';
        }
        if (error.includes('password is entirely numeric') || error.includes('entirely numeric')) {
          return 'Your password cannot be entirely numeric. Please include letters or special characters.';
        }
        if (error.includes('password must contain') || error.includes('must include')) {
          return error; // Keep the specific requirement message
        }
        if (fieldName.includes('password') && error.includes('at least')) {
          const match = error.match(/at least (\d+) characters/);
          if (match) {
            return `Password must be at least ${match[1]} characters long.`;
          }
        }
        if (error.includes('phone') || (error.includes('invalid') && fieldName.includes('phone'))) {
          return 'Please enter a valid phone number.';
        }
        if (error.includes('Ensure this field has at least')) {
          const match = error.match(/at least (\d+) characters/);
          if (match) {
            return `${formattedFieldName} must be at least ${match[1]} characters.`;
          }
        }
        if (error.includes('Ensure this field has no more than')) {
          const match = error.match(/no more than (\d+) characters/);
          if (match) {
            return `${formattedFieldName} must be no more than ${match[1]} characters.`;
          }
        }
        if (error.includes('incorrect') && fieldName.includes('password')) {
          return 'Current password is incorrect.';
        }
        // Return original error if no specific formatting applies
        return error;
      });

      return formattedErrors.join(' ');
    })
    .join('\n');
};

/**
 * Gets a user-friendly error message from an error object
 * @param error - The error object from axios or similar HTTP client
 * @returns User-friendly error message
 */
export const getErrorMessage = (error: ErrorResponse): string => {
  const parsedError = parseDjangoError(error);

  if (parsedError.fieldErrors && Object.keys(parsedError.fieldErrors).length > 0) {
    return formatFieldErrors(parsedError.fieldErrors);
  }

  // Handle common system/backend errors
  if (parsedError.status) {
    if (parsedError.status >= 500) {
      return 'Something went wrong on our end. Please try again later or contact support if the issue persists.';
    }
    if (parsedError.status === 404) {
      return 'The requested resource was not found.';
    }
    if (parsedError.status === 403) {
      return 'You do not have permission to perform this action.';
    }
    if (parsedError.status === 401) {
      return 'Your session has expired. Please log in again.';
    }
  }

  // Return the parsed message or a generic fallback
  return parsedError.message || 'An unexpected error occurred. Please try again.';
};

/**
 * Gets field-specific error for a given field name
 * @param fieldErrors - Object containing field errors
 * @param fieldName - Name of the field to get error for
 * @returns Error message for the field or undefined
 */
export const getFieldError = (
  fieldErrors: { [key: string]: string[] } | undefined,
  fieldName: string
): string | undefined => {
  if (!fieldErrors) return undefined;

  // Check for exact match
  if (fieldErrors[fieldName]) {
    return fieldErrors[fieldName][0];
  }

  // Check for nested field name (e.g., 'user.phone' when fieldName is 'phone')
  for (const [key, errors] of Object.entries(fieldErrors)) {
    if (key.endsWith(`.${fieldName}`) || key === fieldName) {
      return errors[0];
    }
  }

  return undefined;
};
